import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Button,
  Autocomplete,
} from '@mui/material';
import {
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Replay as ReplayIcon,
  ContentCopy as ContentCopyIcon,
  Schedule as ScheduleIcon,
  Computer as ComputerIcon,
  Business as BusinessIcon,
  Message as MessageIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { SirketLookup } from '../components/SirketLookup';
import { TetiklemeDialog, type TetiklemeDialogInitialValues } from '../components/TetiklemeDialog';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles } from '../utils/refreshAnimation';
import { eStatementService } from '../services/eStatementService';
import { agentService } from '../services/agentService';
import { bankServiceRunService } from '../services/bankServiceRunService';
import { bankaService } from '../services/bankaService';
import { formatDateToTR } from '../utils/dateUtils';
import { parseDateRangeFromMessage } from '../utils/parseSonIslemZamaniDates';
import type { SonIslemZamani } from '../types/eStatement';
import type { SirketHesapDto } from '../types/bankServiceRun';
import type { Banka } from '../types/banka';

const SonIslemZamaniPage = () => {
  const { selectedTenantIds, selectedServerIds, refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<SonIslemZamani[]>([]);
  
  // Paging state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [filter, setFilter] = useState<{
    vkn: string;
    uid: string;
    bankaKodu: string;
    erpSirketKodu: string;
    durum: 'all' | boolean;
  }>({
    vkn: '',
    uid: '',
    bankaKodu: '',
    erpSirketKodu: '',
    durum: 'all',
  });

  // Mesaj popup state
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  // Tetikleme popup state (aynı ekranda açılır)
  const [tetiklemeDialogOpen, setTetiklemeDialogOpen] = useState(false);
  const [tetiklemeInitialValues, setTetiklemeInitialValues] = useState<TetiklemeDialogInitialValues | undefined>(undefined);

  const [agents, setAgents] = useState<Array<{ serverId: string; isOnline: boolean }>>([]);
  const [selectedSirketServerId, setSelectedSirketServerId] = useState<string | undefined>(undefined);
  const [sirketHesaplar, setSirketHesaplar] = useState<SirketHesapDto[]>([]);
  const [bankalar, setBankalar] = useState<Banka[]>([]);
  const reachableServerIds = useMemo(
    () => new Set(agents.filter((a) => a.isOnline).map((a) => a.serverId)),
    [agents]
  );

  const selectedServerId = selectedServerIds.length === 1 ? selectedServerIds[0] : undefined;
  const selectedTenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : undefined;

  const loadAgents = useCallback(async () => {
    try {
      const data = await agentService.getAll();
      setAgents(data.map((a) => ({ serverId: a.serverId, isOnline: a.isOnline })));
    } catch {
      setAgents([]);
    }
  }, []);

  const loadSirketHesaplar = useCallback(async (serverId: string, vkn?: string) => {
    try {
      const data = await bankServiceRunService.getSirketHesaplar(serverId, vkn);
      setSirketHesaplar(data);
    } catch {
      setSirketHesaplar([]);
    }
  }, []);

  const loadBankalar = useCallback(async () => {
    try {
      const data = await bankaService.getAll();
      setBankalar(data);
    } catch {
      setBankalar([]);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Filtre parametrelerini hazırla
      const selectedTenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : undefined;
      const selectedServerId = selectedServerIds.length === 1 ? selectedServerIds[0] : undefined;
      
      const result = await eStatementService.getSonIslemZamaniList({
        tenantId: selectedTenantId,
        serverId: selectedServerId,
        vkn: filter.vkn || undefined,
        uid: filter.uid || undefined,
        bankaKodu: filter.bankaKodu ? parseInt(filter.bankaKodu) : undefined,
        erpSirketKodu: filter.erpSirketKodu || undefined,
        durum: filter.durum === 'all' ? undefined : filter.durum,
        page: page + 1, // Backend 1-based, frontend 0-based
        pageSize: pageSize,
      });
      
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantIds, selectedServerIds, filter, page, pageSize]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents, refreshTrigger]);

  useEffect(() => {
    loadBankalar();
  }, [loadBankalar]);

  useEffect(() => {
    const serverIdForHesaplar = selectedSirketServerId || selectedServerId;
    if (serverIdForHesaplar && filter.vkn) {
      loadSirketHesaplar(serverIdForHesaplar, filter.vkn);
    } else {
      setSirketHesaplar([]);
    }
  }, [selectedSirketServerId, selectedServerId, filter.vkn, loadSirketHesaplar]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewMessage = (message: string) => {
    setSelectedMessage(message);
    setMessageDialogOpen(true);
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setSelectedMessage(null);
  };

  const handleTekrarTetikle = (item: SonIslemZamani) => {
    if (!reachableServerIds.has(item.serverId)) return;
    const { beginDate, endDate } = parseDateRangeFromMessage(item.mesaj);
    setTetiklemeInitialValues({
      serverId: item.serverId,
      serviceVkn: item.vkn,
      bankCode: String(item.bankaKodu),
      uid: item.uid,
      beginDate: beginDate || undefined,
      endDate: endDate || undefined,
    });
    setTetiklemeDialogOpen(true);
  };

  const truncateMessage = (message: string, maxLength: number = 50): string => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (loading && items.length === 0) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
        <Box mb={3}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              mb: 1,
            }}
          >
            Son İşlem Zamanı
          </Typography>
          <Typography variant="body2" color="text.secondary">
            eStatement son işlem zamanı kayıtlarını görüntüleyin
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filtreler */}
        <Paper
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <Box display="flex" gap={2} flexWrap="wrap">
            <SirketLookup
              value={filter.vkn}
              onChange={(vkn, serverId) => {
                setSelectedSirketServerId(serverId);
                setFilter((prev) => ({
                  ...prev,
                  vkn,
                  uid: '',
                  bankaKodu: '',
                }));
              }}
              serverId={selectedServerId}
              tenantId={selectedTenantId}
              label="Şirket (VKN)"
              placeholder="Şirket ara..."
              size="small"
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 260px' } }}
            />
            <Autocomplete
              size="small"
              options={sirketHesaplar}
              value={sirketHesaplar.find((h) => h.uid === filter.uid) ?? null}
              getOptionLabel={(h) => h.uid}
              isOptionEqualToValue={(a, b) => a.uid === b.uid}
              onChange={(_, newValue) =>
                setFilter((prev) => ({ ...prev, uid: newValue?.uid ?? '' }))
              }
              disabled={!filter.vkn}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="UID"
                  placeholder={filter.vkn ? 'UID ara...' : 'Önce şirket seçiniz'}
                />
              )}
              noOptionsText="Hesap yok"
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}
            />
            {(() => {
              const bankaKodlari = Array.from(new Set(sirketHesaplar.map((h) => h.bankaKodu)));
              const filtrelenmisBankalar = bankalar.filter((b) => bankaKodlari.includes(b.bankaKodu));
              const bankaOptions = filter.vkn ? filtrelenmisBankalar : bankalar;
              const selectedBanka = bankaOptions.find((b) => String(b.bankaKodu) === filter.bankaKodu) ?? null;
              return (
                <Autocomplete
                  size="small"
                  options={bankaOptions}
                  value={selectedBanka}
                  getOptionLabel={(b) => `${b.bankaAdi} (${b.bankaKodu})`}
                  isOptionEqualToValue={(a, b) => a.bankaKodu === b.bankaKodu}
                  onChange={(_, newValue) =>
                    setFilter((prev) => ({
                      ...prev,
                      bankaKodu: newValue ? String(newValue.bankaKodu) : '',
                    }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Banka"
                      placeholder="Banka ara..."
                    />
                  )}
                  noOptionsText="Banka yok"
                  sx={{ flex: { xs: '1 1 100%', sm: '1 1 220px' } }}
                />
              );
            })()}
            <TextField
              size="small"
              label="ERP Şirket Kodu"
              value={filter.erpSirketKodu}
              onChange={(e) => setFilter((prev) => ({ ...prev, erpSirketKodu: e.target.value }))}
              placeholder="ERP şirket kodu..."
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}
            />
            <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
              <InputLabel>Durum</InputLabel>
              <Select
                value={filter.durum}
                label="Durum"
                onChange={(e) => setFilter((prev) => ({ ...prev, durum: e.target.value as 'all' | boolean }))}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="true">Başarılı</MenuItem>
                <MenuItem value="false">Başarısız</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Tablo - standart şık tablo yapısı */}
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflowX: 'auto', overflowY: 'visible' }}>
            <Table size="small" sx={{ minWidth: 1100, position: 'relative' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[50]) }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 100 }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Tarih
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 120 }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ComputerIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Sunucu
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 95 }}>VKN</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 160 }}>Şirket Ünvanı</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 85 }}>UID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 90 }}>Banka Kodu</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 110 }}>ERP Şirket Kodu</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 115 }}>Son İşlem Tarihi</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider', minWidth: 220 }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <MessageIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Mesaj
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      py: 1.5,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      width: 120,
                      minWidth: 120,
                      maxWidth: 120,
                      position: 'sticky',
                      right: 56,
                      left: 'auto',
                      bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[50]),
                      zIndex: 14,
                      boxShadow: (theme) => (theme.palette.mode === 'dark' ? '-4px 0 8px -2px rgba(0,0,0,0.4)' : '-4px 0 8px -2px rgba(0,0,0,0.12)'),
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <FlagIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Durum
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      py: 1.5,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      width: 56,
                      minWidth: 56,
                      maxWidth: 56,
                      position: 'sticky',
                      right: 0,
                      left: 'auto',
                      bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[50]),
                      zIndex: 15,
                      boxShadow: (theme) => (theme.palette.mode === 'dark' ? '-4px 0 8px -2px rgba(0,0,0,0.4)' : '-4px 0 8px -2px rgba(0,0,0,0.12)'),
                    }}
                    align="right"
                  >
                    <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.75}>
                      <ReplayIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Tetikle
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Kayıt bulunamadı
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow
                      key={item.id}
                      hover
                      sx={(theme) => ({
                        bgcolor:
                          index % 2 === 1
                            ? theme.palette.mode === 'dark'
                              ? theme.palette.background.default
                              : theme.palette.grey[100]
                            : 'transparent',
                        '&:hover': {
                          bgcolor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.paper
                              : theme.palette.grey[200],
                        },
                        '&:hover .sticky-cell-durum, &:hover .sticky-cell-tetikle': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? theme.palette.background.paper
                              : theme.palette.grey[200],
                        },
                        transition: 'background-color 0.15s',
                      })}
                    >
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 100 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {formatDateToTR(item.tarih)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 120 }}>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <ComputerIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          {item.serverName}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 95 }}>{item.vkn}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 160, maxWidth: 200 }} title={item.sirketUnvani ?? ''}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {item.sirketUnvani ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 85 }}>{item.uid}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 90 }}>{item.bankaKodu}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 110 }}>{item.erpSirketKodu}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 115 }}>
                        {item.sonIslemTarihi ? formatDateToTR(item.sonIslemTarihi) : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem', py: 1.25, minWidth: 220 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <MessageIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: item.mesaj.length > 50 ? 'pointer' : 'default' }}
                            onClick={() => item.mesaj.length > 50 && handleViewMessage(item.mesaj)}
                          >
                            {truncateMessage(item.mesaj)}
                          </Typography>
                          {item.mesaj.length > 50 && (
                            <Tooltip title="Tam mesajı görüntüle">
                              <IconButton size="small" onClick={() => handleViewMessage(item.mesaj)} sx={{ p: 0.5 }} aria-label="Mesaj detayı">
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        className="sticky-cell-durum"
                        sx={{
                          fontSize: '0.8rem',
                          py: 1.25,
                          width: 120,
                          minWidth: 120,
                          maxWidth: 120,
                          position: 'sticky',
                          right: 56,
                          left: 'auto',
                          backgroundColor: (theme) => {
                            if (theme.palette.mode === 'dark') {
                              return index % 2 === 1 ? theme.palette.background.default : theme.palette.background.paper;
                            }
                            return index % 2 === 1 ? theme.palette.grey[100] : theme.palette.background.paper;
                          },
                          zIndex: 12,
                          boxShadow: (theme) => (theme.palette.mode === 'dark' ? '-4px 0 8px -2px rgba(0,0,0,0.4)' : '-4px 0 8px -2px rgba(0,0,0,0.12)'),
                        }}
                      >
                        <Chip
                          icon={item.durum ? <CheckCircleIcon /> : <CancelIcon />}
                          label={item.durum ? 'Başarılı' : 'Başarısız'}
                          color={item.durum ? 'success' : 'error'}
                          size="small"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell
                        className="sticky-cell-tetikle"
                        sx={{
                          fontSize: '0.8rem',
                          py: 1.25,
                          width: 56,
                          minWidth: 56,
                          maxWidth: 56,
                          position: 'sticky',
                          right: 0,
                          left: 'auto',
                          backgroundColor: (theme) => {
                            if (theme.palette.mode === 'dark') {
                              return index % 2 === 1 ? theme.palette.background.default : theme.palette.background.paper;
                            }
                            return index % 2 === 1 ? theme.palette.grey[100] : theme.palette.background.paper;
                          },
                          zIndex: 13,
                          boxShadow: (theme) => (theme.palette.mode === 'dark' ? '-4px 0 8px -2px rgba(0,0,0,0.4)' : '-4px 0 8px -2px rgba(0,0,0,0.12)'),
                        }}
                        align="right"
                      >
                        <Tooltip
                          title={
                            reachableServerIds.has(item.serverId)
                              ? 'Banka servis tetikleme (popup)'
                              : 'Sunucuya erişilemiyor (online agent yok)'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleTekrarTetikle(item)}
                              disabled={!reachableServerIds.has(item.serverId)}
                              aria-label="Tekrar tetikle"
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangePageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage="Sayfa başına kayıt:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `~${to}`}`}
          />
        </Paper>

        {/* Mesaj Dialog */}
        <Dialog
          open={messageDialogOpen}
          onClose={handleCloseMessageDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
            <span>Mesaj Detayı</span>
            <Tooltip title="Mesajı kopyala">
              <IconButton
                size="small"
                onClick={() => {
                  if (selectedMessage) void navigator.clipboard.writeText(selectedMessage);
                }}
                disabled={!selectedMessage}
                aria-label="Mesajı kopyala"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {selectedMessage}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMessageDialog}>Kapat</Button>
          </DialogActions>
        </Dialog>

        {/* Banka servis tetikleme popup - aynı ekranda açılır, tetikle/kapat sonrası burada kalır */}
        <TetiklemeDialog
          open={tetiklemeDialogOpen}
          onClose={() => {
            setTetiklemeDialogOpen(false);
            setTetiklemeInitialValues(undefined);
          }}
          initialValues={tetiklemeInitialValues}
          onSuccess={() => loadData()}
        />
      </Box>
    </Layout>
  );
};

export default SonIslemZamaniPage;
